import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '../utils/cn';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
function getRowKey(row, keyAccessor) {
  if (typeof keyAccessor === 'function') return keyAccessor(row);
  return row[keyAccessor];
}
function getCellValue(row, column) {
  if (column.render) return column.render(row);
  if (column.accessor) return row[column.accessor];
  return null;
}
function SortIcon({ direction, active }) {
  const className = cn('ml-1 inline', active ? 'text-fg-secondary' : 'text-fg-tertiary');
  if (!active) {
    return <ChevronsUpDown size={16} className={className} aria-hidden="true" />;
  }
  if (direction === 'asc') {
    return <ChevronUp size={16} className={className} aria-hidden="true" />;
  }
  return <ChevronDown size={16} className={className} aria-hidden="true" />;
}
export function DataTable({
  columns,
  data,
  keyAccessor,
  sortColumn,
  sortDirection,
  onSort,
  selectable = false,
  selectedRows,
  onSelectionChange,
  isRowSelectable,
  disabledRowTooltip,
  bulkActions,
  pagination,
  loading = false,
  loadingRows = 5,
  emptyIcon,
  emptyTitle = 'No data',
  emptyDescription,
  emptyAction,
  className,
}) {
  const selectableKeys = React.useMemo(
    () =>
      data
        .filter((row) => !isRowSelectable || isRowSelectable(row))
        .map((row) => getRowKey(row, keyAccessor)),
    [data, keyAccessor, isRowSelectable],
  );
  const allKeys = React.useMemo(
    () => data.map((row) => getRowKey(row, keyAccessor)),
    [data, keyAccessor],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedRows?.has(k));
  const someSelected = (selectedRows?.size ?? 0) > 0 && !allSelected;
  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableKeys));
    }
  };
  const handleSelectRow = (key) => {
    if (!onSelectionChange || !selectedRows) return;
    const next = new Set(selectedRows);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  };
  const handleSort = (columnId) => {
    if (!onSort) return;
    const nextDirection = sortColumn === columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(columnId, nextDirection);
  };
  const hasSelection = (selectedRows?.size ?? 0) > 0;
  return (
    <div className={cn('overflow-hidden rounded-lg border border-edge-strong', className)}>
      {/* Bulk action bar */}
      {selectable && hasSelection && bulkActions && (
        <div className="flex items-center gap-3 border-b border-edge-strong bg-surface-muted px-4 py-2">
          <span className="text-sm font-medium text-fg">{selectedRows.size} selected</span>
          <div className="flex items-center gap-2">{bulkActions}</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-edge-strong">
          <thead className="bg-surface-secondary">
            <tr>
              {selectable && (
                <th scope="col" className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-edge text-fg focus:ring-2 focus:ring-neutral-500/25"
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  className={cn(
                    'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-fg-tertiary',
                    col.sortable && 'cursor-pointer select-none',
                    col.className,
                  )}
                  onClick={col.sortable ? () => handleSort(col.id) : undefined}
                  aria-sort={
                    sortColumn === col.id
                      ? sortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <span className="inline-flex items-center">
                    {col.header}
                    {col.sortable && (
                      <SortIcon
                        direction={sortColumn === col.id ? sortDirection : undefined}
                        active={sortColumn === col.id}
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-edge-strong bg-surface">
            {loading ? (
              Array.from({
                length: loadingRows,
              }).map((_, i) => (
                <tr key={i}>
                  {selectable && (
                    <td className="px-4 py-3">
                      <Skeleton variant="rect" className="h-4 w-4 rounded" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.id} className="px-4 py-3">
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-8">
                  <EmptyState
                    icon={emptyIcon}
                    title={emptyTitle}
                    description={emptyDescription}
                    action={emptyAction}
                  />
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const key = getRowKey(row, keyAccessor);
                const isSelected = selectedRows?.has(key) ?? false;
                const rowSelectable = !isRowSelectable || isRowSelectable(row);
                return (
                  <tr
                    key={key}
                    className={cn(
                      'transition-colors hover:bg-surface-secondary',
                      isSelected && 'bg-surface-secondary',
                    )}
                  >
                    {selectable && (
                      <td className="px-4 py-3">
                        <span title={!rowSelectable && disabledRowTooltip ? disabledRowTooltip(row) : undefined}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!rowSelectable}
                            onChange={() => handleSelectRow(key)}
                            className={cn(
                              'h-4 w-4 rounded border-edge text-fg focus:ring-2 focus:ring-neutral-500/25',
                              !rowSelectable && 'cursor-not-allowed opacity-50',
                            )}
                            aria-label={`Select row ${key}`}
                          />
                        </span>
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          'whitespace-nowrap px-4 py-3 text-sm text-fg-secondary',
                          col.className,
                        )}
                      >
                        {getCellValue(row, col)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-edge-strong bg-surface px-4 py-3">
          <p className="text-sm text-fg-tertiary">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              className="rounded-md border border-edge px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              className="rounded-md border border-edge px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
